import type { GridCell, InterdictionRequest, InterdictionResult } from "./interdiction";
import { shortestPath } from "./interdiction";
import { buildInterdictionLP } from "./interdictionModel";
import { solveLp } from "./highsWorker";

/**
 * Solve a shortest-path interdiction instance with HiGHS, returning the
 * keeper's proven-best wall set, the forced shortest-path length, and the
 * hero's resulting path. Runs in the shared Web Worker so the host game's UI
 * never freezes. The path and length are recomputed from the decoded walls by
 * BFS, so they are exactly consistent with what the game draws.
 */
export async function solveInterdiction(req: InterdictionRequest): Promise<InterdictionResult> {
  const { timeLimitSec = 30 } = req;

  const t0 = performance.now();
  const { lp, cellOf } = buildInterdictionLP(req);
  const tBuildMs = performance.now() - t0;

  const { sol, solveMs: tSolveMs } = await solveLp(lp, {
    time_limit: timeLimitSec,
    presolve: "on",
  });

  const walls: GridCell[] = [];
  if (sol.Status === "Optimal" || sol.Status === "Feasible") {
    for (const [cid, cell] of cellOf) {
      const col = sol.Columns[`z_${cid}`];
      if (col && col.Primal > 0.5) walls.push(cell);
    }
  }

  // Recompute the hero's path under the chosen walls — the honest ground truth.
  const sp = shortestPath(req, walls);
  const status =
    sol.Status === "Optimal" ? "OPTIMAL" :
    sol.Status === "Feasible" ? "FEASIBLE" :
    String(sol.Status).toUpperCase();

  return {
    status,
    length: sp ? sp.length : 0,
    walls,
    path: sp ? sp.path : [],
    tBuildMs,
    tSolveMs,
  };
}
